import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  MessageSquare,
  Send,
  Mail,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  Clock,
  Shield,
  HeadphonesIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export function ContactPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    category: 'general',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'billing', label: 'Billing Question' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.subject.trim() || !formData.message.trim()) {
      setNotification({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }

    if (formData.message.length < 10) {
      setNotification({
        type: 'error',
        message: 'Message must be at least 10 characters long'
      });
      return;
    }

    try {
      setSending(true);
      setNotification(null);

      // Import EmailService dynamically to avoid circular dependencies
      const { EmailService } = await import('../lib/email-service');

      await EmailService.send({
        to: 'support@rulevia.com',
        template: 'support-request' as any,
        userId: user?.id,
        data: {
          userName: user?.name || 'User',
          userEmail: user?.email || '',
          userId: user?.id || '',
          category: formData.category,
          categoryLabel: categories.find(c => c.value === formData.category)?.label || 'General Inquiry',
          subject: formData.subject,
          message: formData.message,
          planName: user?.subscription_plan || 'Free Trial',
          sentAt: new Date().toISOString()
        }
      });

      // Clear form and show success
      setFormData({
        category: 'general',
        subject: '',
        message: ''
      });

      setNotification({
        type: 'success',
        message: 'Your message has been sent successfully! We\'ll get back to you within 24 hours.'
      });
    } catch (error) {
      console.error('Failed to send support request:', error);
      setNotification({
        type: 'error',
        message: 'Failed to send message. Please try again or email us directly at support@rulevia.com'
      });
    } finally {
      setSending(false);
    }
  };

  // Auto-dismiss notifications after 10 seconds
  React.useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Helmet>
        <title>Contact Support - Rulevia</title>
        <meta name="description" content="Get help from our support team. Contact us for technical support, billing questions, feature requests, and more." />
      </Helmet>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 sm:mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <MessageSquare className="w-8 h-8 text-purple-600" />
              <span>Contact Support</span>
            </h1>
            <p className="text-gray-600 text-sm sm:text-base mt-1">
              Get help from our support team. We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Contact Form - Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            {/* Notification */}
            {notification && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${
                  notification.type === 'success' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {notification.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <p className={`text-sm ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your issue or question"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.subject.length}/200 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Please provide as much detail as possible. Include steps to reproduce any issues, error messages, and what you expected to happen."
                  maxLength={5000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[120px] resize-y"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/5000 characters (minimum 10)
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sending || !formData.subject.trim() || !formData.message.trim() || formData.message.length < 10}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] font-medium"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </motion.div>

        {/* Sidebar - Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          {/* Quick Contact Info */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4 sm:p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Direct Contact</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Email Support:</p>
                <a 
                  href="mailto:support@rulevia.com" 
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  support@rulevia.com
                </a>
              </div>
            </div>
          </div>

          {/* Response Times */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Response Times</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">General Inquiries:</span>
                <span className="font-medium text-gray-900">24 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Technical Issues:</span>
                <span className="font-medium text-gray-900">12 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Billing Questions:</span>
                <span className="font-medium text-gray-900">6 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Agency/Enterprise:</span>
                <span className="font-medium text-purple-600">Priority</span>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Privacy & Security</h3>
            </div>
            <p className="text-sm text-gray-600">
              Your messages are encrypted and handled securely. We never share your information 
              with third parties and only use it to provide support.
            </p>
          </div>

          {/* Self-Service */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <HeadphonesIcon className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Quick Help</h3>
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-gray-600">Before contacting support, try:</p>
              <ul className="space-y-1 text-gray-600 ml-4">
                <li>• Check your internet connection</li>
                <li>• Refresh the page</li>
                <li>• Clear browser cache</li>
                <li>• Try a different browser</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}